import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic, useStore } from '@tanstack/react-form';
import { useState } from 'react';
import { useMatch } from 'react-router';
import { z } from 'zod';

import {
  mutateCurrentInstanceUpdate,
  useCurrentInstance,
  useCurrentInstanceSettings,
  type InstanceDetailDataType,
  type InstanceGQLResponse,
  type InstanceShortDataType,
} from '@/api/organization';
import CompanySettingsGeneralInfo from '@/app/company-settings/CompanySettingsGeneralInfo';
import PageError from '@/components/errors/PageError';
import { useAppForm } from '@/components/Form';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import Prompt from '@/components/Prompt';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

import CompanyImpactScoringSettings from './CompanyImpactScoringSettings';
import CompanyNonDigitalEventsSettings from './CompanyNonDigitalEventsSettings';
import CompanyOpportunitySettings from './CompanyOpportunitySettings';
import CompanySettingsActionBar from './CompanySettingsActionBar';
import CompanySettingsTag from './CompanySettingsTag';
import CompanySettingsTAM from './CompanySettingsTAM';
import CompanyVisitExclusions from './CompanyVisitExclusions';

type CompanySettingsContentProps = {
  instance: InstanceShortDataType | undefined;
  instanceDetails: InstanceDetailDataType;
  mutate: (
    data: InstanceGQLResponse<InstanceShortDataType>,
    opts?: { revalidate?: boolean },
  ) => void;
  mutateDetails: (
    data?: InstanceGQLResponse<InstanceDetailDataType>,
    opts?: { revalidate?: boolean },
  ) => void;
};

const getFormSchema = () => {
  const taskEventSchema = z.object({
    offlineEventsAll: z.boolean(),
    offlineTasksAll: z.boolean(),
    offlineTasksSpecific: z.array(z.string()),
  });

  const leadSchema = z.object({
    offlineEventsLeadStatus: z
      .string()
      .trim()
      .min(1, t`Lead Status is required`),
  });

  const campaignSchema = z.object({
    typeField: z
      .string()
      .trim()
      .min(1, t`Campaign Field is required`),
    spendField: z
      .string()
      .trim()
      .min(1, t`Campaign Spend Field is required`),
  });

  return z.object({
    id: z.string(),
    name: z
      .string()
      .trim()
      .min(1, t`Company name is required`),
    tam: z.object({
      audience: z.union([
        z.null(),
        z.object({
          id: z.string().min(1, t`Audience is required`),
          name: z.string().optional(),
        }),
      ]),
    }),
    code: z.string(),
    domain: z.string(),
    createdAt: z.string(),
    hasActiveCRMConnection: z.boolean(),
    taskTypeOptions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    leadStatusOptions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    typeFieldOptions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    spendFieldOptions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    pipelineReport: z
      .object({
        url: z.string().nullish(),
        fieldName: z.string().nullish(),
        influenceWindow: z
          .number({ error: t`Influence window is required` })
          .min(1, t`Influence window must be at least 1 day`)
          .max(365, t`Influence window must be less than 1 year`),
      })
      .nullable(),
    taskEvent: taskEventSchema.nullish(),
    lead: leadSchema.nullish(),
    campaign: campaignSchema.nullish(),
    visitExclusions: z.array(
      z
        .string()
        .trim()
        .min(1, t`URL exclusion path is required (e.g. /careers)`),
    ),
  });
};

const CompanySettingsContent = ({
  instance,
  instanceDetails,
  mutate,
  mutateDetails,
}: CompanySettingsContentProps) => {
  const { pushNotification, removeNotification } = useNotification();
  const user = useMe();
  const navigate = useInstanceNavigate();
  const isImpactScoresRoute = useMatch(ROUTES.companySettingsImpactScore.path) != null;
  const [isGeneralOptionsEditMode, setIsGeneralOptionsEditMode] = useState(false);
  const [isImpactScoringDirty, setIsImpactScoringDirty] = useState(false);
  const [isImpactScoringEditMode, setIsImpactScoringEditMode] = useState(false);
  const [isImpactScoringSubmitting, setIsImpactScoringSubmitting] = useState(false);
  const activeTab = isImpactScoresRoute ? 'impact-scores' : 'general';
  const form = useAppForm({
    defaultValues: instanceDetails,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: getFormSchema(),
      onSubmitAsync: async ({ value, formApi }) => {
        removeNotification({ type: 'error' });

        try {
          await mutateCurrentInstanceUpdate(value);

          pushNotification({
            type: 'success',
            message: t`Success! Company settings have been saved.`,
          });
          removeNotification({ type: 'error' });

          setIsGeneralOptionsEditMode(false);
          formApi.reset(value, { keepDefaultValues: true });

          mutateDetails({
            instances: {
              current: {
                ...instanceDetails,
                ...value,
              },
            },
          });
          if (instance && value.tam.audience) {
            mutate(
              {
                instances: {
                  current: {
                    ...instance,
                    name: value.name,
                    pipelineReport: value.pipelineReport,
                    tam: {
                      audience: { ...value.tam.audience, isTAM: true },
                    },
                  },
                },
              },
              { revalidate: false },
            );
          }
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`Sorry, we were unable to update your settings due to an error. Please review your changes and retry.`,
          });
        }
      },
    },
    onSubmitInvalid: () => {
      pushNotification({
        type: 'error',
        message: t`Sorry, we were unable to update your settings due to an error. Please review your changes and retry.`,
      });
    },
  });
  const { isDirty: isGeneralOptionsDirty, isSubmitting: isGeneralOptionsSubmitting } = useStore(
    form.store,
    (state) => ({ isDirty: state.isDirty, isSubmitting: state.isSubmitting }),
  );

  const handleEditChange = (desiresEditMode: boolean) => {
    setIsGeneralOptionsEditMode(desiresEditMode);

    if (desiresEditMode) {
      removeNotification({ type: 'success' });
    }
  };

  const handleCancel = () => {
    form.reset(instanceDetails, { keepDefaultValues: true });
    handleEditChange(false);
  };

  const handleTabChange = (tabValue: string) => {
    if (
      activeTab === 'general' &&
      isGeneralOptionsEditMode &&
      isGeneralOptionsDirty &&
      !isGeneralOptionsSubmitting
    ) {
      return;
    }

    if (
      activeTab === 'impact-scores' &&
      isImpactScoringEditMode &&
      isImpactScoringDirty &&
      !isImpactScoringSubmitting
    ) {
      return;
    }

    navigate(
      tabValue === 'impact-scores'
        ? ROUTES.companySettingsImpactScore.path
        : ROUTES.companySettingsGeneral.path,
    );
  };

  const tabsList = (
    <TabsList variant="line" aria-label={t`Company settings sections`}>
      <TabsTrigger value="general">
        <Trans>General Options</Trans>
      </TabsTrigger>
      <TabsTrigger value="impact-scores">
        <Trans>Impact Scoring</Trans>
      </TabsTrigger>
    </TabsList>
  );

  const generalSettings = (
    <form
      className="flex h-full flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Prompt
          message={t`Are you sure you want to exit without saving your company settings?`}
          shouldPreventNavigation={
            isGeneralOptionsEditMode && isGeneralOptionsDirty && !isGeneralOptionsSubmitting
          }
        />
        <CompanySettingsActionBar
          isEditMode={isGeneralOptionsEditMode}
          isSaveEnabled={isGeneralOptionsDirty}
          isSaving={isGeneralOptionsSubmitting}
          startContent={tabsList}
          showActions={!!user?.isAdmin}
          onEditModeChange={handleEditChange}
          onCancel={handleCancel}
        />
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2">
          <div>
            <CompanySettingsGeneralInfo
              instance={instanceDetails}
              isEditMode={isGeneralOptionsEditMode}
            />
          </div>
          <div>
            <CompanySettingsTAM isEditMode={isGeneralOptionsEditMode} />
          </div>
          <div className="md:col-span-2">
            <Panel title={t`Custom Opportunity Settings`} data-testid="opp-settings-panel">
              <CompanyOpportunitySettings
                instance={instanceDetails}
                isEditMode={isGeneralOptionsEditMode}
              />
            </Panel>
          </div>
          <div className="md:col-span-2">
            <Panel title={t`Non-Digital Events`} data-testid="non-digital-events-panel">
              <CompanyNonDigitalEventsSettings
                isEditMode={isGeneralOptionsEditMode}
                instance={instanceDetails}
              />
            </Panel>
          </div>
          <div className="md:col-span-2">
            <CompanyVisitExclusions isEditMode={isGeneralOptionsEditMode} />
          </div>
          <div className="md:col-span-2">
            <CompanySettingsTag instance={instanceDetails} />
          </div>
        </div>
      </form.AppForm>
    </form>
  );

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      <TabsContent value="general">{generalSettings}</TabsContent>

      <TabsContent value="impact-scores">
        <CompanyImpactScoringSettings
          canEdit={!!user?.isAdmin}
          startContent={tabsList}
          onDirtyChange={setIsImpactScoringDirty}
          onEditModeChange={setIsImpactScoringEditMode}
          onSubmittingChange={setIsImpactScoringSubmitting}
        />
      </TabsContent>
    </Tabs>
  );
};

const CompanySettings = () => {
  const { instanceDetails, isLoading, error, mutate: mutateDetails } = useCurrentInstanceSettings();
  const { instance, mutate } = useCurrentInstance();

  return (
    <Page
      title={t`Settings`}
      pageName={t`Your Company`}
      exampleAIQuestions={exampleAIQuestions('companySettings')}
    >
      {isLoading && <PageSpinner />}
      {!isLoading && !!error && (
        <PageError
          message={<Trans>An error occurred while loading your Company Settings</Trans>}
          detail={<Trans>Please refresh the page and try again</Trans>}
          headingLevel={2}
        />
      )}
      {!isLoading && !error && !!instanceDetails && (
        <CompanySettingsContent
          instance={instance}
          instanceDetails={instanceDetails}
          mutate={mutate}
          mutateDetails={mutateDetails}
        />
      )}
    </Page>
  );
};

export default CompanySettings;
